import { useCallback, useEffect, useRef, useState } from 'react';
import { useSiteWriteAccess } from '../../hooks/useSiteWriteAccess';
import { apiService } from '../../services/ApiService';
import { getPOIElementDbId } from '../../utils/POIFieldExtractor';
import { ThumbnailQueue } from '../../services/thumbnails/ThumbnailQueue';
import { logger } from '../../utils/log';
import { LMV_SDK_SCRIPT_URL } from '../../viewers/autodesk/lmvSdk';

/**
 * useElementThumbnails — THE element-picture generator (Option D, 2026-07-14).
 *
 * Owns a dedicated HIDDEN headless viewer (its own off-screen div + Viewer3D
 * instance) that photographs elements one by one: isolate → fitToView →
 * settle → screenshot → upload. It shares NOTHING with the main AutodeskViewer
 * component except the already-initialized global Autodesk.Viewing environment
 * — no camera hijacking, no state save/restore in the main viewer.
 *
 * Callers stay dumb: enqueueThumbnails(poiIds) with DB ids. The generator
 * resolves dbIds + already-photographed state from a fresh /pois fetch per
 * batch (refreshed once per unknown id — late enqueues are never blacklisted
 * by a stale snapshot), so triggers never thread model data around.
 *
 * Failure containment (review-hardened):
 *  - Batch-level failures (env missing, viewer/model won't load) do NOT consume
 *    per-element attempts; they schedule a delayed retry, bounded at
 *    MAX_BATCH_RETRIES per site (counter resets on any successful capture).
 *  - Per-element failures consume the queue's 2-attempt budget and re-queue at
 *    the BACK so one broken element cannot block a batch.
 *  - Site switch bumps a generation token: in-flight work stops, the old pump
 *    hands off to the new generation's pump if work arrived while it held the
 *    pumping latch.
 *
 * Deliberate Alpha constraints: photographs the site's PRIMARY model urn only;
 * POIs without a resolvable viewer dbId are skipped permanently (icon fallback
 * remains). The LMV environment is self-bootstrapped (script + Initializer)
 * when the session never opened the 3D viewer — see the bootstrap block below.
 */

const CAPTURE_SIZE = 320;
const SETTLE_MS = 900;               // camera/render settle after fitToView
const PER_ELEMENT_TIMEOUT_MS = 20_000;
const MODEL_LOAD_TIMEOUT_MS = 180_000;
const BATCH_RETRY_MS = 30_000;
const MAX_BATCH_RETRIES = 3;         // per site; reset on any successful capture

interface ViewerConfigLike {
  urn?: string;
  connectionId?: string;
  assetId?: string;
  accRegion?: 'US' | 'EMEA';
}

// ---------------------------------------------------------------------------
// Self-contained LMV bootstrap. The generator must work WITHOUT the user ever
// opening the 3D viewer (product requirement: Set-Systems → pictures, no extra
// steps), so it can inject the SDK script and run the Initializer itself —
// mirroring AutodeskViewer's exact options (env AutodeskProduction2, region-
// keyed api, /acc/viewer-token accessor). Module-level promises make both
// steps idempotent; if the main viewer initialized first, Initializer resolves
// immediately and its (equivalent) token accessor stays in charge.
// ---------------------------------------------------------------------------

let lmvScriptPromise: Promise<void> | null = null;
let lmvInitPromise: Promise<void> | null = null;

const loadLmvScript = (): Promise<void> => {
  if ((window as any).Autodesk?.Viewing) return Promise.resolve();
  if (!lmvScriptPromise) {
    lmvScriptPromise = new Promise<void>((resolve, reject) => {
      // Same pinned SDK as AutodeskViewer, whichever of the two loads it first.
      const script = document.createElement('script');
      script.src = LMV_SDK_SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        lmvScriptPromise = null; // allow a later attempt
        reject(new Error('failed to load Autodesk viewer SDK'));
      };
      document.head.appendChild(script);
    });
  }
  return lmvScriptPromise;
};

const initializeLmv = (config: ViewerConfigLike): Promise<void> => {
  if (!lmvInitPromise) {
    lmvInitPromise = new Promise<void>((resolve, reject) => {
      const Viewing = (window as any).Autodesk?.Viewing;
      if (!Viewing?.Initializer) {
        lmvInitPromise = null;
        reject(new Error('Autodesk.Viewing.Initializer unavailable'));
        return;
      }
      Viewing.Initializer({
        env: 'AutodeskProduction2',
        api: config.accRegion === 'EMEA' ? 'streamingV2_EU' : 'streamingV2',
        useLocalCache: false,
        getAccessToken: (onTokenReady: (token: string, expiresIn: number) => void) => {
          apiService.get(`/acc/viewer-token?connectionId=${config.connectionId}&assetId=${config.assetId}`)
            .then(data => onTokenReady(data.access_token, data.expires_in))
            .catch(err => logger.warn('[thumbnails] token fetch failed:', err?.message));
        },
      }, () => resolve());
    });
  }
  return lmvInitPromise;
};

interface UseElementThumbnailsOptions {
  assetId?: string;
  /** Primary-model config of the current site (undefined = no ACC model). */
  viewerConfig?: ViewerConfigLike | null;
}

export interface UseElementThumbnailsResult {
  /** Request pictures for these POIs by DB id (idempotent; photographed POIs are skipped). */
  enqueueThumbnails: (poiIds: Array<string | number>) => void;
  /** Bumps once per drained batch that stored pictures — consumers re-fetch the URL map. */
  thumbnailsVersion: number;
}

const withTimeout = <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> =>
  Promise.race([
    promise,
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)),
  ]);

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

export const useElementThumbnails = ({ assetId, viewerConfig }: UseElementThumbnailsOptions): UseElementThumbnailsResult => {
  /**
   * Thumbnails are stored on the server (poi.thumbnail — a writer's capability), so for a
   * read-only member every upload in the batch answered 403 while the viewer quietly
   * captured image after image. Nothing broke; the console filled with failures that hid
   * real ones, and the work was wasted. A reader now simply does not run the batch — the
   * pictures they see are the ones somebody with write access already stored.
   */
  const canWrite = useSiteWriteAccess(assetId);
  const [thumbnailsVersion, setThumbnailsVersion] = useState(0);

  const queueRef = useRef(new ThumbnailQueue());
  const pumpingRef = useRef(false);
  // Generation token: bumped on site switch/unmount — in-flight work notices and stops.
  const generationRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const batchRetriesRef = useRef(0);
  // Always points at the LATEST pump closure — the generation-handoff path in a
  // stale pump's finally block must not re-run with captured old assetId/urn.
  const pumpRef = useRef<() => void>(() => {});

  const viewerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const loadedUrnRef = useRef<string | null>(null);

  const teardownViewer = useCallback(() => {
    try {
      viewerRef.current?.finish?.();
    } catch (err) {
      logger.warn('[thumbnails] viewer teardown error (ignored):', err);
    }
    viewerRef.current = null;
    loadedUrnRef.current = null;
    containerRef.current?.remove();
    containerRef.current = null;
  }, []);

  /** Bounded delayed retry for batch-level conditions (env/model unavailable). */
  const scheduleBatchRetry = useCallback((reason: string) => {
    if (retryTimerRef.current) return;
    if (batchRetriesRef.current >= MAX_BATCH_RETRIES) {
      logger.warn('[thumbnails] batch retry budget exhausted — queue parked until next trigger', { reason });
      return;
    }
    batchRetriesRef.current++;
    logger.info('[thumbnails] batch retry scheduled', { reason, attempt: batchRetriesRef.current });
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      pumpRef.current();
    }, BATCH_RETRY_MS);
  }, []);

  /** Create the hidden viewer and load the site's primary model. */
  const ensureViewer = useCallback(async (urn: string): Promise<any> => {
    const Viewing = (window as any).Autodesk?.Viewing;
    if (!Viewing?.Document) throw new Error('LMV environment not initialized');
    if (viewerRef.current && loadedUrnRef.current === urn) return viewerRef.current;
    teardownViewer();

    // Off-screen but NOT display:none — WebGL needs a laid-out canvas.
    const div = document.createElement('div');
    div.style.cssText = `position:fixed;left:-10000px;top:0;width:${CAPTURE_SIZE}px;height:${CAPTURE_SIZE}px;`;
    document.body.appendChild(div);
    containerRef.current = div;

    const viewer = new Viewing.Viewer3D(div);
    const startCode = viewer.start();
    if (startCode !== 0) {
      teardownViewer();
      throw new Error(`hidden viewer failed to start (code ${startCode})`);
    }
    viewerRef.current = viewer;

    const doc: any = await withTimeout(new Promise((resolve, reject) => {
      Viewing.Document.load(
        urn.startsWith('urn:') ? urn : `urn:${urn}`,
        (d: any) => resolve(d),
        (code: any, message: any) => reject(new Error(`Document.load failed: ${code} ${message || ''}`))
      );
    }), MODEL_LOAD_TIMEOUT_MS, 'document load');

    const geometry = doc.getRoot().getDefaultGeometry();
    if (!geometry) throw new Error('model has no default geometry');

    const model: any = await withTimeout(
      viewer.loadDocumentNode(doc, geometry),
      MODEL_LOAD_TIMEOUT_MS,
      'model load'
    );

    // isolate/fitToView/screenshot need the instance tree AND streamed geometry
    // (the tree appears long before meshes finish on large models — capturing
    // early persists blank thumbnails that would then be skipped forever).
    // Self-terminating poll: dies on its own deadline and within one tick of a
    // teardown (viewerRef changes), so no timer chain outlives the viewer.
    await new Promise<void>((resolve, reject) => {
      const startedAt = Date.now();
      const check = () => {
        if (viewerRef.current !== viewer) {
          reject(new Error('viewer torn down during model readiness wait'));
          return;
        }
        if (Date.now() - startedAt >= MODEL_LOAD_TIMEOUT_MS) {
          reject(new Error('model readiness timed out'));
          return;
        }
        const treeReady = Boolean(model.getInstanceTree?.());
        const geomReady = typeof model.isLoadDone !== 'function' || model.isLoadDone();
        if (treeReady && geomReady) resolve();
        else setTimeout(check, 500);
      };
      check();
    });

    loadedUrnRef.current = urn;
    logger.info('[thumbnails] hidden viewer ready', { urn: urn.slice(0, 24) });
    return viewer;
  }, [teardownViewer]);

  /** Photograph one element and return a JPEG data URL. */
  const captureElement = useCallback(async (viewer: any, dbId: number): Promise<string> => {
    viewer.isolate([dbId]);
    viewer.fitToView([dbId], viewer.model, false);
    await delay(SETTLE_MS);

    const blobUrl: string = await new Promise((resolve, reject) => {
      try {
        viewer.getScreenShot(CAPTURE_SIZE, CAPTURE_SIZE, (url: string) => {
          if (url) resolve(url);
          else reject(new Error('getScreenShot returned nothing'));
        });
      } catch (err) {
        reject(err);
      }
    });

    try {
      const blob = await (await fetch(blobUrl)).blob();
      const bitmap = await createImageBitmap(blob);
      // Re-encode PNG screenshot → white-backed JPEG (small, no alpha surprises).
      const canvas = document.createElement('canvas');
      canvas.width = CAPTURE_SIZE;
      canvas.height = CAPTURE_SIZE;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('2d context unavailable');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, CAPTURE_SIZE, CAPTURE_SIZE);
      ctx.drawImage(bitmap, 0, 0, CAPTURE_SIZE, CAPTURE_SIZE);
      return canvas.toDataURL('image/jpeg', 0.8);
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  }, []);

  const pump = useCallback(async () => {
    if (pumpingRef.current) return;
    const generation = generationRef.current;
    const aId = assetId;
    const urn = viewerConfig?.urn;
    const queue = queueRef.current;
    if (!aId || queue.size === 0) return;
    // Every upload in this batch would 403 (see canWrite above). Drop the queue rather than
    // capture pictures nobody can store.
    if (!canWrite) { queue.clear(); return; }
    if (!urn) {
      logger.info('[thumbnails] parked: no primary model urn for this site — queue kept');
      return;
    }
    pumpingRef.current = true;
    let storedCount = 0;
    // POIs that already HAD pictures (e.g. a re-seeded system): the client may
    // not have their URLs yet, so recognizing them must also trigger a refetch.
    let recognizedExisting = 0;
    try {
      // Ensure the LMV environment ourselves (script + Initializer) so the flow
      // works without the 3D viewer ever being opened. Needs a connection to
      // mint tokens; without one, fall back to an env the main viewer may have
      // initialized, else park on the bounded retry.
      if (viewerConfig?.connectionId) {
        try {
          await loadLmvScript();
          await initializeLmv(viewerConfig);
        } catch (err: any) {
          logger.warn('[thumbnails] LMV bootstrap failed — batch parked', { error: err?.message });
          scheduleBatchRetry('lmv-bootstrap-failed');
          return;
        }
        if (generationRef.current !== generation) return;
      } else if (!(window as any).Autodesk?.Viewing?.Document) {
        scheduleBatchRetry('lmv-env-not-ready');
        return;
      }
      // Fresh snapshot of the site's POIs: resolves dbIds and filters
      // already-photographed elements. Ids enqueued AFTER this fetch get one
      // snapshot refresh before being treated as genuinely unknown.
      const pois = await apiService.get(`/assets/${aId}/pois`);
      if (generationRef.current !== generation) return;
      const byId = new Map<string, any>();
      for (const p of Array.isArray(pois) ? pois : []) byId.set(String(p.id), p);
      const refreshedIds = new Set<string>();

      let viewer: any = null;
      while (queue.size > 0 && generationRef.current === generation) {
        const task = queue.peek();
        if (!task) break;

        let poi = byId.get(task.poiId);
        if (!poi) {
          if (refreshedIds.has(task.poiId)) {
            queue.skip(task.poiId); // absent even after refresh — deleted/foreign id
            continue;
          }
          refreshedIds.add(task.poiId);
          const fresh = await apiService.get(`/assets/${aId}/pois`);
          if (generationRef.current !== generation) break;
          for (const p of Array.isArray(fresh) ? fresh : []) byId.set(String(p.id), p);
          continue;
        }
        if (poi.thumbnail_key) { queue.complete(task.poiId); recognizedExisting++; continue; }
        const dbId = getPOIElementDbId(poi);
        if (!dbId) { queue.skip(task.poiId); continue; }

        // Batch-level guard: a viewer/model that won't come up must not burn
        // per-element attempts — park the batch and retry on the bounded timer.
        if (!viewer) {
          try {
            viewer = await ensureViewer(urn);
          } catch (err: any) {
            logger.warn('[thumbnails] viewer/model unavailable — batch parked', { error: err?.message });
            scheduleBatchRetry('viewer-unavailable');
            break;
          }
          if (generationRef.current !== generation) break;
        }

        try {
          const imageDataUrl = await withTimeout(
            captureElement(viewer, dbId),
            PER_ELEMENT_TIMEOUT_MS,
            `capture poi ${task.poiId}`
          );
          if (generationRef.current !== generation) break;
          await apiService.put(`/assets/${aId}/pois/${task.poiId}/thumbnail`, { imageDataUrl });
          queue.complete(task.poiId);
          storedCount++;
          batchRetriesRef.current = 0; // progress resets the batch-retry budget
          // Progressive canvas updates: long batches must not hold every picture
          // hostage until the end (URL-map refetches are merge-cheap client-side).
          if (storedCount % 5 === 0) setThumbnailsVersion(v => v + 1);
          logger.debug('[thumbnails] stored', { poiId: task.poiId, dbId });
        } catch (err: any) {
          logger.warn('[thumbnails] capture failed', { poiId: task.poiId, error: err?.message });
          queue.fail(task.poiId);
        }
      }
    } catch (err: any) {
      logger.warn('[thumbnails] pump aborted:', err?.message);
    } finally {
      pumpingRef.current = false;
      logger.info('[thumbnails] batch finished', { stored: storedCount, recognizedExisting, remaining: queueRef.current.size });
      // Final bump covers the batch tail AND already-photographed equipment the
      // client didn't have URLs for (re-seeded systems, prior sessions).
      if (storedCount > 0 || recognizedExisting > 0) setThumbnailsVersion(v => v + 1);
      if (generationRef.current !== generation) {
        teardownViewer();
        // A newer generation enqueued while we held the pumping latch and its
        // pump() no-oped — hand off through the ref (fresh assetId/urn).
        if (queueRef.current.size > 0) pumpRef.current();
      } else if (queueRef.current.size === 0) {
        teardownViewer(); // free the model/GPU as soon as the batch drains
      }
    }
  }, [assetId, canWrite, viewerConfig, ensureViewer, captureElement, teardownViewer, scheduleBatchRetry]);

  useEffect(() => { pumpRef.current = () => void pump(); }, [pump]);

  const enqueueThumbnails = useCallback((poiIds: Array<string | number>) => {
    const added = queueRef.current.enqueue(poiIds.map(String));
    if (added > 0) void pump();
  }, [pump]);

  // Site switch / unmount: abandon pending work and release the hidden viewer.
  useEffect(() => {
    return () => {
      // generationRef is a hook-local counter, not a DOM node: the cleanup MUST bump the
      // live value so an in-flight pump sees the change — snapshotting it would defeat it.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      generationRef.current++;
      queueRef.current = new ThumbnailQueue();
      batchRetriesRef.current = 0;
      if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
      teardownViewer();
    };
  }, [assetId, teardownViewer]);

  // A urn appearing later (viewer config loads after enqueue) unblocks the queue.
  useEffect(() => {
    if (viewerConfig?.urn && queueRef.current.size > 0) void pump();
  }, [viewerConfig?.urn, pump]);

  return { enqueueThumbnails, thumbnailsVersion };
};
